import type {
  AnswerOptionDto,
  ApplicationDecision,
  GetRentalApplicationsParams as LeasingGetRentalApplicationsParams,
  InfoUpdateRequestDto,
  PaymentStatus,
  RentalApplicationDto,
  RentalApplicationFileDto,
  ReportStatus,
  ReportType,
} from '~/swagger/leasing/Api';
import type { AddressDto } from '~/swagger/portal/Api';
import type { RentalApplicationFileRequirement } from '~/swagger/settings/Api';

export interface GeneralQuestion {
  id: string;
  text: string;
  isRequired: boolean;
  isMultiSelect: boolean;
  answers: GeneralAnswer[];
}

export interface GeneralAnswer {
  id: string;
  text: string;
  isSelected: boolean;
  isFreeFormInput: boolean;
  freeformValue?: string | null;
  followUpQuestions: GeneralQuestion[];
}

export enum QuestionCategories {
  General = 'general',
  Employment = 'employment',
}

export interface QuestionTemplateUpsertDto {
  category: QuestionCategories;
  text: string;
  isRequired: boolean;
  isMultiSelect: boolean;
  sortOrder: number;
  answers: AnswerOptionDto[];
}

export interface RentalApplicationQuestionTemplateDto {
  id: string;
  category: QuestionCategories;
  text: string;
  isRequired: boolean;
  isMultiSelect: boolean;
  sortOrder: number;
  answers: AnswerOptionDto[];
}

export type RentalApplicationFileRequirementLocal = RentalApplicationFileRequirement;

export type IRentalApplicationDto = Omit<RentalApplicationDto, 'applicationQuestions'> & {
  applicationQuestions: Map<QuestionCategories, GeneralQuestion[]>;
  requiresTwoPhaseScreening?: boolean;
  criminalScreeningReleasedAt?: string | null;
  criminalScreeningReleasedByUserId?: string | null;
  paymentStatus?: PaymentStatus;
  applicationDecision?: ApplicationDecision;
  hasBeenSubmitted?: boolean;
  draft?: boolean;
  reportsStatuses?: RentalApplicationReportStatusDto[];
  rentalApplicationTransactionDto?: {
    externalTransactionType?: string;
    transactionId?: string;
    externalTransactionId?: string;
    paymentDate?: string;
    transactionAmount?: number;
    transactionStatus?: string;
  } | null;
  refundAmount?: number;
  screeningStatus?: ScreeningStatus;
  interests?: RentalApplicationInterestLike[];
  tenant?: { id?: string; firstName?: string; lastName?: string } | null;
  lease?: { id?: string } | null;
  infoUpdateRequests?: InfoUpdateRequestDto[];
  paymentAmount?: number;
  aiApplicationDescription?: string;
  applyingWith?: unknown;
};

export interface RentalApplicationInterestUnitLike {
  id: string;
  name?: string;
  imageId?: string;
  singleLineAddress?: string;
  icons?: unknown[];
}

export interface RentalApplicationInterestPropertyLike {
  id?: string;
  name?: string;
  address?: AddressDto | null;
}

export interface RentalApplicationInterestLike {
  id?: string;
  unit: RentalApplicationInterestUnitLike;
  property: RentalApplicationInterestPropertyLike;
  unitId?: string;
  propertyId?: string;
  portfolioId?: string;
}

export interface InfoUpdateRequestLocalDto {
  id: string;
  reason?: string;
  status?: string;
  emailSentAt?: string;
  textSentAt?: string;
  createdAt?: string;
  expiresAt?: string;
  requestedAt?: string;
  code?: string;
}

export type IQuestionnaireSettingsDto = {
  applicationQuestions: Map<QuestionCategories, GeneralQuestion[]>;
};

export interface RentalApplicationPaymentStatusDto {
  paid: boolean;
  paymentStatus?: PaymentStatus;
  amountPaid?: number;
  paidAt?: string;
}

export interface RentalApplicationCountsDto {
  newRentalApplications?: number;
  allApplications?: number;
  approvedApplications?: number;
  deniedApplications?: number;
}

export interface PublicCountry {
  id: string;
  name?: string;
  code?: string;
}

export interface RentalApplicationReportStatusDto {
  reportType: ReportType;
  reportStatus: ReportStatus;
  errorMessage?: string | null;
}

export interface UpdateRentalApplicationFileDto {
  fileId?: string;
  name?: string;
  description?: string;
}

export enum CommentSection {
  ApplicantInformation = 'applicantInformation',
  RentalHistory = 'rentalHistory',
  WorkHistory = 'workHistory',
  Cars = 'cars',
  Pets = 'pets',
  Questions = 'questions',
  Identity = 'identity',
  Other = 'other',
}

export enum ScreeningStatus {
  NotStarted = 'notStarted',
  UnVerified = 'unVerified',
  Verified = 'verified',
  ManualVerificationRequired = 'manualVerificationRequired',
  Skipped = 'skipped',
}

export type GetRentalApplicationsParams = Partial<LeasingGetRentalApplicationsParams> & {
  draft?: boolean;
  hasBeenSubmitted?: boolean;
  new?: boolean;
  hasName?: boolean;
  tenantId?: string;
};

export type { RentalApplicationFileDto };

declare module '~/swagger/leasing/Api' {
  interface GetRentalApplicationsParams {
    newOnly?: boolean;
  }

  interface RentalApplicationListDto {
    applicationDecision?: ApplicationDecision;
    createdAt?: string;
    hasBeenSubmitted?: boolean;
    draft?: boolean;
    interests?: RentalApplicationInterestLike[];
    incomeInformation?: { annualIncome?: number; monthlyIncome?: number };
    tenant?: { id?: string; firstName?: string; lastName?: string } | null;
  }

  interface RentalApplicationInterestDto {
    unit?: RentalApplicationInterestUnitLike;
    property?: RentalApplicationInterestPropertyLike;
  }

  interface RentalApplicationEmploymentDto {
    position?: string;
  }

  interface InfoUpdateRequestDto {
    emailSentAt?: string;
    textSentAt?: string;
    requestedAt?: string;
    code?: string;
  }

  interface RentalApplicationQuestionItemDto {
    options?: string[];
    type?: string;
  }

  interface RentalApplicationFileDto {
    fileName?: string;
    fileUrl?: string;
    thumbUrl?: string;
  }

  interface CreateRentalApplicationDto {
    ssn?: string;
    dateOfBirth?: string;
    applyingWith?: unknown;
    desiredMoveInDate?: string;
    maritalStatus?: unknown;
    driversLicense?: unknown;
    incomeInformation?: unknown;
    pets?: unknown[];
    cars?: unknown[];
    residentialHistory?: unknown[];
    emergencyContact?: unknown;
    employment?: unknown[];
    applicationQuestions?: unknown;
    draft?: boolean;
    tenantId?: string;
    interestedUnitIds?: string[];
    decision?: ApplicationDecision;
    files?: unknown[];
  }
}
